import { Box, Drawer } from '@material-ui/core';
import { VipStarIcon } from 'assets/icons';
import cn from 'classnames';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import AvatarCard from 'features/Avatars/AvatarCard';
import CpActionCards from 'features/customerPanel/cpActionCards';
import MessageCard from 'features/customerPanel/MessageCard';
import PanelVerificationAlerts from 'features/customerPanel/panelVerficiationAlert/PanelVerificationAlert';
import { clearBalanceData } from 'features/customerPanel/store/cpPanel.action';
import { UserPanel } from 'features/installPwa';
import { getAvatarProfile, getVerificationStatus, getVipData } from 'features/login/store/login.action';
import { fetchBonusesAndFreebets } from 'features/offers/store/offers.action';
import { selectHasAnyOffers } from 'features/offers/store/offers.selectors';
import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps, useHistory, withRouter } from 'react-router-dom';
import Button from 'shared/components/form/button';
import {
	ACCOUNT_DOCUMENT_STATUS_ACCEPTED,
	APP_URL,
	AWAITING_ADMIN_APPROVAL,
	customerAccountStatus,
	SectionNavigation,
} from 'shared/constants/common';
import { InboxEvents, setDataLayer } from 'shared/constants/datalayer';
import { useNewVerificationPanelFlag } from 'shared/featureFlags';
import useSectionNavigation from 'shared/hooks/useSectionNavigation';
import { useVipUserCheck } from 'shared/hooks/useVipUserCheck';
import { BalanceState, CouponState, State } from 'shared/interface';
import { updateUserDetails } from 'shared/store/coupon.action';
import { openDrawer } from 'shared/store/drawer.action';
import { generateUserInitials, getUserDetails, handleAddOverflow, handleRemoveOverflow, isLoggedin, isMobile, logout } from 'shared/util/utility';
import FinancialOverview from './FinancialOverview';
import SessionTimer from './SessionTimer';

const CpCoupons = lazy(() => import('features/customerPanel/cpCoupons/container/cpCoupons'));
const CpPersonalData = lazy(() => import('features/customerPanel/cpPersonalData/container/cpPersonalData'));
const CpBonusDesktop = lazy(() => import('features/customerPanel/cpBonus/container/cpBonusDesktop'));
const MessagesInbox = lazy(() => import('features/customerPanel/cpMessages/container/MessagesInbox'));
const CpMarketingConsent = lazy(() => import('features/customerPanel/cpMarketingConsent'));
const CpResponsibleGamingDesktop = lazy(() => import('features/customerPanel/cpRegulationsDesktop/container/cpRegulationsDesktop'));
const CpPayoutDesktop = lazy(() => import('features/customerPanel/cpPayout/container/cpPayoutDesktop'));
const CpPromoCode = lazy(() => import('features/customerPanel/cpPromoCode/container/cpPromoCode'));
const CpChangePassword = lazy(() => import('features/customerPanel/cpChangePassword'));
const ActivityHistory = lazy(() => import('features/customerPanel/cpActivityHistory'));
const CpLimits = lazy(() => import('features/customerPanel/cpLimits/container/cpLimits'));
const CpSettings = lazy(() => import('features/customerPanel/cpSettings'));

interface PanelProps extends RouteComponentProps {
	balanceData: BalanceState;
	showPanel: boolean;
}

const Panel = (props: PanelProps) => {
	const isLogin = isLoggedin();
	const history = useHistory();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const userDetails = useMemo(() => getUserDetails(), [isLogin]);
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { vipData, isVip, vipLevelArray, vipUserInitalsBgClass } = useVipUserCheck();
	const unreadMessageCount = useSelector<State, number>((state) => state.navigation.unreadMessages.messageCount);
	const unreadMessageTitle = useSelector<State, string>((state) => state.navigation.unreadMessages.lastMessageTitle);
	const unreadMessageDate = useSelector<State, string>((state) => state.navigation.unreadMessages.lastMessageDate);
	const messageId = useSelector<State, number>((state) => state.navigation.unreadMessages.lastMessageId);
	const verificationStatus = useSelector<State, string>((state) => state.login.accountDetail?.account?.verificationStatus);
	const userAvatar = useSelector((state: RootStateOrAny) => state.playerProfile.avatar);
	const userRegistrationDate = useSelector((state: RootStateOrAny) => state.login.accountDetail.account?.registrationDetails?.regDate);
	const today = new Date();
	const registrationDate = new Date(userRegistrationDate);
	const daysSinceRegistration = differenceInCalendarDays(today, registrationDate);
	const hasAnyOffers = useSelector(selectHasAnyOffers);
	const [dialogOpen, setDialogOpen] = useState<boolean>(false);
	const { isViewActive, isChildViewActive, changeNavigationView, goToFirstNavigationView } = useSectionNavigation();
	const coupon = useSelector<State, CouponState>((state) => state.coupon);
	const newVerificationPanelFlag = useNewVerificationPanelFlag();

	const renderCpTab = () => {
		if (isMobile()) {
			return null;
		}

		if (isViewActive(SectionNavigation.CP_COUPON)) {
			return <CpCoupons closeCpPanel={closePanel} />;
		} else if (isViewActive(SectionNavigation.CP_PERSONAL_DATA)) {
			return (
				<CpPersonalData
					handleClosePanel={closePanel}
					showVerificationModal={() => {
						newVerificationPanelFlag?.value
							? history.push(APP_URL.verification, window.location.pathname)
							: goToFirstNavigationView(SectionNavigation.ACCOUNT_VERIFICATION_METHOD);
					}}
				/>
			);
		} else if (isViewActive(SectionNavigation.CP_BONUS)) {
			return <CpBonusDesktop handleClosePanel={closePanel} />;
		} else if (isViewActive(SectionNavigation.CP_MESSAGES)) {
			return <MessagesInbox handleClosePanel={closePanel} />;
		} else if (isViewActive(SectionNavigation.CP_MARKETING_CONSENT)) {
			return <CpMarketingConsent handleClosePanel={closePanel} />;
		} else if (isViewActive(SectionNavigation.CP_REGULATIONS)) {
			return <CpResponsibleGamingDesktop handleClosePanel={closePanel} />;
		} else if (isViewActive(SectionNavigation.CP_PAYOUT)) {
			return <CpPayoutDesktop handleClosePanel={closePanel} />;
		} else if (isViewActive(SectionNavigation.CP_PROMO_CODE)) {
			return <CpPromoCode handleClosePanel={closePanel} />;
		} else if (isViewActive(SectionNavigation.CP_CHANGE_PASSWORD)) {
			return <CpChangePassword handleClosePanel={closePanel} />;
		} else if (isViewActive(SectionNavigation.CP_ACTIVITY_HISTORY)) {
			return <ActivityHistory handleClosePanel={closePanel} />;
		} else if (isViewActive(SectionNavigation.CP_RESPONSIBLE_GAME)) {
			return <CpLimits handleClosePanel={closePanel} />;
		} else if (isViewActive(SectionNavigation.CP_SETTINGS)) {
			return <CpSettings handleClosePanel={closePanel} />;
		} else {
			closePanel();
			return <></>;
		}
	};

	useEffect(() => {
		if (isLogin) {
			dispatch(getVipData());
			dispatch(getAvatarProfile());
		}
	}, [dispatch, isLogin]);

	useEffect(() => {
		if (isLogin) {
			if (!verificationStatus || verificationStatus === '') {
				dispatch(getVerificationStatus());
			}

			if (props.showPanel) {
				!hasAnyOffers && dispatch(fetchBonusesAndFreebets());
			}
		}
	}, [props.showPanel, isLogin, verificationStatus, dispatch, hasAnyOffers]);

	useEffect(() => {
		if (isViewActive(SectionNavigation.USER, true)) {
			handleAddOverflow();
		}
	}, [isViewActive]);

	useEffect(() => {
		if (!props.showPanel) {
			handleRemoveOverflow();
		}
	}, [props.showPanel]);

	const onLogOutSubmit = async () => {
		try {
			const response = await logout(coupon);
			if (response && response.status === 200) {
				dispatch(clearBalanceData());
				dispatch(updateUserDetails());
				if (window.location.pathname === APP_URL.deposit) {
					history.push(`${APP_URL.home}`);
				} else if (history.location.hash === '#section=user') {
					history.goBack();
					setTimeout(() => {
						window.location.reload();
					}, 0);
				} else {
					window.location.reload();
				}
			}
		} catch (err) {
			console.error('err', err);
		}
	};

	const closePanel = () => {
		handleRemoveOverflow();
		history.goBack();
	};
	const closeBackdropPanel = () => {
		handleRemoveOverflow();
		if (history.location.hash.includes('#section=user/')) {
			history.go(-2);
		} else {
			history.goBack();
		}
	};

	if (!isLogin) {
		return <></>;
	}

	const vipStarComponent = [...Array(5)].map((star, i) => {
		const rating = i + 1;
		return rating <= vipLevelArray[vipData] ? (
			<VipStarIcon className="vipStars vipStars--filled" />
		) : (
			<VipStarIcon className="vipStars vipStars--notFilled" />
		);
	});

	const rangiVipRedirection = () => {
		if (isVip) {
			history.push(APP_URL.rangiVip);
		}
	};

	const playerProfileRedirection = () => {
		if (isMobile()) {
			history.push(APP_URL.playerProfile);
		} else {
			setDialogOpen(true);
		}
	};

	const handleUserAvatarClick = () => {
		playerProfileRedirection();
		dispatch(openDrawer(true));
	};

	return (
		<Drawer
			className={`sidenav-backdrop ${!isMobile() && 'sidenav-desktop'}`}
			anchor={'right'}
			open={props.showPanel}
			onClose={closeBackdropPanel}
			ModalProps={{
				disableEscapeKeyDown: true,
				keepMounted: false,
			}}
			disableEnforceFocus={true}
		>
			<Box className="cp-menu-box" sx={{ width: `${isMobile() ? '300px' : '430px'}` }} role="presentation" onClick={closePanel}>
				{isChildViewActive(SectionNavigation.USER) ? (
					<div className="cp-menu" onClick={(e) => e.stopPropagation()}>
						<Suspense fallback={<></>}>{renderCpTab()}</Suspense>
					</div>
				) : (
					<>
						<div
							className={`login-nav height--full flex flex--column align__items--center ${!isMobile() && 'login-nav-desktop'}`}
							onClick={(e) => e.stopPropagation()}
						>
							<div className="userAvatarCard">
								<AvatarCard
									userAvatar={userAvatar}
									playerName={userDetails.firstName}
									description={`${t('registrationInfo', { totalDays: daysSinceRegistration })} ${t('day', {
										count: daysSinceRegistration,
									})}! 🤩`}
									playerInitials={generateUserInitials(userDetails.firstName, userDetails.lastName)}
									vipUserBackground={vipUserInitalsBgClass}
									isRedirection={true}
									isVip={isVip}
									onAvatarClick={handleUserAvatarClick}
									redirectionUrl={playerProfileRedirection}
									renderVipRankStars={vipStarComponent && vipStarComponent}
								/>
							</div>
							<div
								className={cn('PanelVerificationAlert', { 'cursor-pointer': verificationStatus !== AWAITING_ADMIN_APPROVAL })}
								onClick={() => {
									if (verificationStatus === AWAITING_ADMIN_APPROVAL) {
										return;
									}
									newVerificationPanelFlag?.value
										? history.push(APP_URL.verification, window.location.pathname)
										: goToFirstNavigationView(SectionNavigation.ACCOUNT_VERIFICATION_METHOD);
								}}
							>
								<PanelVerificationAlerts customerAccountDetails={userDetails} customerAccountStatus={customerAccountStatus} />
							</div>
							<div className="user-panel-pay-options">
								<UserPanel className={cn('-mt-0.5 pb-2', { 'pt-1': verificationStatus !== ACCOUNT_DOCUMENT_STATUS_ACCEPTED })} />
								<FinancialOverview
									onAction={changeNavigationView}
									handleRemoveOverflow={handleRemoveOverflow}
									balanceData={props.balanceData}
								/>
							</div>
							<MessageCard
								redirection={() => {
									changeNavigationView(SectionNavigation.CP_MESSAGES);
									setDataLayer(
										InboxEvents.DATALAYER_INBOX_OPEN(
											window.location.pathname,
											window.location.pathname.split('/').slice(1, 3).join(','),
										),
									);
								}}
								messageDetailRedirection={() => {
									changeNavigationView(SectionNavigation.CP_MESSAGES_DISPLAY, { messageId });
									setDataLayer(InboxEvents.DATALAYER_OPEN_MESSAGE_LAST(true));
								}}
								newMessageTitle={unreadMessageTitle}
								unreadMessageDate={unreadMessageDate}
								unreadMessageCount={unreadMessageCount}
								cyTestRef="cp-panel-list"
							/>
							<CpActionCards
								isVip={isVip}
								rangiVipRedirection={rangiVipRedirection}
								changeNavigationView={changeNavigationView}
								dialogOpen={dialogOpen}
								setDialogOpen={setDialogOpen}
								handleClosePanel={() => closePanel()}
								history={history}
							/>
							<div className="logout-control">
								<Button btnType="ghost" btnHeight="small" cyTestRef="cp-logout" onClick={onLogOutSubmit}>
									{t('logout')}
								</Button>
								<SessionTimer sessionStart={userDetails?.sessionStart} />
							</div>
						</div>
					</>
				)}
			</Box>
		</Drawer>
	);
};

export default withRouter(Panel);
